import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ErrorPage } from '@screentone/addon-auth-wrapper';
import { useQueryClient } from '@tanstack/react-query';

import { ResponsiveLoader } from 'components';
import { AddEntity } from 'components/context-menu-actions/add-entity/AddEntity';
import { AddLinkedItem } from 'components/context-menu-actions/add-linked-item/AddLinkedItem';
import { ClearItems } from 'components/context-menu-actions/clear-all/ClearItems';
import { CopyPublishedURL } from 'components/context-menu-actions/copy-published-url/CopyPublishedURL';
import { EditAltSumm } from 'components/context-menu-actions/edit-alt-summ/EditAltSumm';
import { EditLinkedItem } from 'components/context-menu-actions/edit-linked-item/EditLinkedItem';
import { ExcludeFromQuery } from 'components/context-menu-actions/exclude-from-query/ExcludeFromQuery';
import { MoveManualArticleToQueryArticle } from 'components/context-menu-actions/move-manual-article-to-query-article/MoveManualArticleToQueryArticle';
import { MoveQueryArticleToManualArticle } from 'components/context-menu-actions/move-query-article-to-manual-article/MoveQueryArticleToManualArticle';
import { OpenInCMS } from 'components/context-menu-actions/open-in-cms/OpenInCMS';
import { OpenInNewsGrid } from 'components/context-menu-actions/open-in-newsgrid/OpenInNewsGrid';
import { RemoveEntity } from 'components/context-menu-actions/remove-entity/RemoveEntity';
import { ViewPublishedURL } from 'components/context-menu-actions/view-published-url/ViewPublishedURL';
import ExternalLink, { ExternalLinkContentType } from 'components/external-link/ExternalLink';
import { useAlert } from 'contexts/alert/useAlert';
import { AllessehContentUpdateProvider } from 'contexts/allesseh-content-update/AllessehContentUpdateContext';
import {
  ContextMenuActionsProvider,
  MenuActions,
  MenuActionsKeys
} from 'contexts/context-menu-actions/ContextMenuActionsContext';
import { DataModelProvider } from 'contexts/datamodel/DataModelContext';
import { DragAndDropProvider } from 'contexts/drag-and-drop/DragAndDropContext';
import { PageLockProvider } from 'contexts/page-lock/PageLockContext';
import { usePageLock } from 'contexts/page-lock/usePageLock';
import { SummarianProvider } from 'contexts/summarian/SummarianContext';
import { TrashProvider } from 'contexts/trash/TrashContext';
import {
  ArticleItem,
  AvailableFeatureFlags,
  CollectionDto,
  LinkedItem,
  ModuleContainer,
  useCollectionDtoByIdQuery,
  useCollectionDtoCreateMutation
} from 'data/generated/graphql';
import { CollectionEdit } from 'features/collection-edit/CollectionEdit';
import { useConvertedProperty } from 'hooks';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { useCommandFSearch } from 'hooks/useCommandFSearch';
import { reportError } from 'utils/errorBoundary';
import { safelyParseContent } from 'utils/temp';

const CollectionEditPage = ({ allessehId, isNewCollection }: { allessehId: string; isNewCollection: boolean }) => {
  const currentProperty = useConvertedProperty();
  const [fetchError, setFetchError] = useState<string>('');
  const [collectionDTO, setCollectionDTO] = useState<CollectionDto | null>(null);
  const { mutateAsync: collectionCreateMutationAsync, isLoading: isCreating } = useCollectionDtoCreateMutation();
  const { alertError } = useAlert();
  const navigate = useNavigate();
  const pageLockState = usePageLock();
  const { state } = pageLockState;
  const { isReadOnly } = state.publishing;

  const contextActions = useMemo<MenuActions>(
    () => ({
      [MenuActionsKeys.CollectionList]: [
        {
          component: ({ hierarchyId, entity, extraProperties }) => {
            const { setOpen = () => {} } = extraProperties!;
            const module = entity as ModuleContainer;
            return (
              <AddLinkedItem
                hierarchyId={hierarchyId!}
                module={module}
                postAction={() => setOpen(false)}
                viewOnly={isReadOnly}
              />
            );
          }
        },
        {
          component: ({ hierarchyId, extraProperties }) => {
            const { setOpen = () => {} } = extraProperties!;
            return (
              <ClearItems
                label="Clear all items from list"
                hierarchyId={hierarchyId!}
                postAction={() => setOpen(false)}
                disabled={isReadOnly}
              />
            );
          },
          showIf: ({ entity }) => {
            const module = entity as ModuleContainer;
            return module.collection.length > 0;
          },
          withDivider: 'top'
        }
      ],
      [MenuActionsKeys.DraftLinkedItem]: [
        {
          component: ({ hierarchyId }) => {
            if (!hierarchyId) {
              return <></>;
            }

            return <RemoveEntity hierarchyId={hierarchyId} text="Remove from collection" disabled={isReadOnly} />;
          },
          withDivider: 'bottom'
        },
        {
          component: ({ entity }) => <EditLinkedItem uuid={(entity as LinkedItem).uuid} />
        },
        {
          component: ({ entity }) => {
            const linkedItem = entity as LinkedItem;
            return <ViewPublishedURL externalLink={linkedItem.attributes.hosted_url} />;
          }
        }
      ],
      [MenuActionsKeys.HistoryLinkedItem]: [
        {
          component: ({ entity }) => {
            const linkedItem = entity as LinkedItem;
            return <ViewPublishedURL externalLink={linkedItem.attributes.hosted_url} />;
          }
        }
      ],
      [MenuActionsKeys.DraftArticle]: [
        {
          component: ({ hierarchyId }) => (
            <RemoveEntity hierarchyId={hierarchyId!} text="Remove from collection" disabled={isReadOnly} />
          ),
          showIf: ({ extraProperties }) => extraProperties!.isContentIdExcludedInQueryRules === false,
          withDivider: 'bottom'
        },
        {
          component: ({ hierarchyId, extraProperties }) => (
            <MoveManualArticleToQueryArticle
              hierarchyId={hierarchyId!}
              jsonQueryStr={extraProperties!.jsonQueryStr!}
              allessehContent={extraProperties!.allessehContent!}
              setJsonQueryStr={extraProperties!.setJsonQueryStr!}
              disabled={isReadOnly}
            />
          ),
          showIf: ({ extraProperties }) => extraProperties!.isContentIdExcludedInQueryRules === true,
          withDivider: 'bottom'
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <EditAltSumm content={safelyParseContent<AllessehContent>(article)} viewOnly={isReadOnly} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <OpenInCMS content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <OpenInNewsGrid content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <ViewPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
          },
          showIf: ({ entity }) => {
            const article = entity as ArticleItem;
            return article.attributes.product !== 'YDJ';
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <CopyPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
          },
          showIf: ({ entity }) => {
            const article = entity as ArticleItem;
            return article.attributes.product !== 'YDJ';
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            const content = safelyParseContent<AllessehContent>(article);

            return (
              <ExternalLink
                contentId={content.data.id}
                isPublished={!!content.data.attributes.published_datetime}
                type={ExternalLinkContentType.ARTICLE}
                embargo={content.data.attributes.content_status === 'embargo'}
              />
            );
          }
        }
      ],
      [MenuActionsKeys.CollectionDraftQueryArticle]: [
        {
          component: ({ entity, hierarchyId, extraProperties }) => (
            <MoveQueryArticleToManualArticle
              article={entity as ArticleItem}
              hierarchyId={hierarchyId!}
              jsonQueryStr={extraProperties!.jsonQueryStr!}
              allessehContent={extraProperties!.allessehContent!}
              setJsonQueryStr={extraProperties!.setJsonQueryStr!}
              disabled={isReadOnly}
            />
          )
        },
        {
          component: ({ extraProperties }) => (
            <ExcludeFromQuery
              jsonQueryStr={extraProperties!.jsonQueryStr!}
              allessehContent={extraProperties!.allessehContent!}
              setJsonQueryStr={extraProperties!.setJsonQueryStr!}
              disabled={isReadOnly}
            />
          ),
          withDivider: 'bottom'
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <EditAltSumm content={safelyParseContent<AllessehContent>(article)} viewOnly={isReadOnly} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <OpenInCMS content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <OpenInNewsGrid content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <ViewPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
          },
          showIf: ({ entity }) => {
            const article = entity as ArticleItem;
            return article.attributes.product !== 'YDJ';
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <CopyPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
          },
          showIf: ({ entity }) => {
            const article = entity as ArticleItem;
            return article.attributes.product !== 'YDJ';
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            const content = safelyParseContent<AllessehContent>(article);

            return (
              <ExternalLink
                contentId={content.data.id}
                isPublished={!!content.data.attributes.published_datetime}
                type={ExternalLinkContentType.ARTICLE}
                embargo={content.data.attributes.content_status === 'embargo'}
              />
            );
          }
        }
      ],
      [MenuActionsKeys.ArticleSearch]: [
        {
          component: ({ hierarchyId, entity, extraProperties }) => (
            <AddEntity
              entity={entity!}
              hierarchyId={hierarchyId!}
              jsonQueryStr={extraProperties!.jsonQueryStr!}
              allessehContent={extraProperties!.allessehContent!}
              setJsonQueryStr={extraProperties!.setJsonQueryStr!}
              disabled={isReadOnly}
            />
          ),
          withDivider: 'bottom'
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <EditAltSumm content={safelyParseContent<AllessehContent>(article)} viewOnly={isReadOnly} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <OpenInCMS content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <OpenInNewsGrid content={safelyParseContent<AllessehContent>(article)} />;
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <ViewPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
          },
          showIf: ({ entity }) => {
            const article = entity as ArticleItem;
            return article.attributes.product !== 'YDJ';
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            return <CopyPublishedURL content={safelyParseContent<AllessehContent>(article)} />;
          },
          showIf: ({ entity }) => {
            const article = entity as ArticleItem;
            return article.attributes.product !== 'YDJ';
          }
        },
        {
          component: ({ entity }) => {
            const article = entity as ArticleItem;
            const content = safelyParseContent<AllessehContent>(article);

            return (
              <ExternalLink
                contentId={content.data.id}
                isPublished={!!content.data.attributes.published_datetime}
                type={ExternalLinkContentType.ARTICLE}
                embargo={content.data.attributes.content_status === 'embargo'}
              />
            );
          }
        }
      ]
    }),
    [isReadOnly]
  );

  const {
    data,
    isLoading,
    isFetching,
    error: loadingError
  } = useCollectionDtoByIdQuery(
    { id: allessehId, publicationKey: currentProperty ?? '' },
    { enabled: !isNewCollection && !!allessehId && !!currentProperty, cacheTime: 0 }
  );

  // if the Allesseh ID is a section page, redirect the user to prevent them from overwriting the collection
  useEffect(() => {
    if (loadingError) {
      reportError('Error fetching collection data: ', loadingError as Error);
      navigate(`/${currentProperty}/pages/${allessehId}`);
    }
  }, [allessehId, currentProperty, loadingError, navigate]);

  const isCollectionDTOLoading = !collectionDTO || (isNewCollection ? isCreating : isLoading || isFetching);

  const createCollection = async (publicationKey: string) => {
    const collection = await collectionCreateMutationAsync({
      collectionDTOCreateInput: {
        name: 'Unnamed Page Section Collection',
        publicationKey
      }
    });
    setCollectionDTO(collection.collectionDTOCreate as CollectionDto);
  };

  // Enables article cards to be searched for using their Allesseh IDs via "command + f"
  useCommandFSearch();

  useEffect(() => {
    if (isCollectionDTOLoading) {
      setCollectionDTO(null);
    }
  }, [isCollectionDTOLoading]);

  useEffect(() => {
    if (isNewCollection && currentProperty) {
      createCollection(currentProperty).catch(() => {
        alertError('Could not create a collection. Please try again.');
        navigate(`/ ${currentProperty} / collections`);
      });
    }
  }, [currentProperty, isNewCollection]); /* eslint-disable-line react-hooks/exhaustive-deps */

  useEffect(() => {
    if (loadingError) {
      reportError('Error fetching collection data: ', loadingError as Error);
      let error = '404';
      if (loadingError instanceof Error) {
        if (loadingError.message === 'Failed to fetch') {
          error = '503';
        }
      }
      setFetchError(error);
    }
  }, [loadingError]);

  useEffect(() => {
    // Also depend on isFetching so that if a refetch completes with the same data reference
    // (React Query structural sharing), the effect still fires and collectionDTO is restored.
    // Without this, invalidateQueries on claim returns identical data, setCollectionDTO is never
    // called, collectionDTO stays null, and the loading screen never clears.
    if (!isFetching && data?.collectionDTOById) {
      setCollectionDTO(data.collectionDTOById as CollectionDto);
    }
  }, [data, isFetching]);

  if (fetchError) {
    return <ErrorPage type={String(fetchError)} />;
  }

  return (
    <>
      {isCollectionDTOLoading ? (
        <ResponsiveLoader />
      ) : (
        <AllessehContentUpdateProvider>
          <SummarianProvider>
            <DataModelProvider type="CollectionDTO" root={collectionDTO.root} metadata={collectionDTO.metadata}>
              <TrashProvider>
                <DragAndDropProvider>
                  <ContextMenuActionsProvider actions={contextActions}>
                    <CollectionEdit />
                  </ContextMenuActionsProvider>
                </DragAndDropProvider>
              </TrashProvider>
            </DataModelProvider>
          </SummarianProvider>
        </AllessehContentUpdateProvider>
      )}
    </>
  );
};

const CollectionEditPageWrapper = () => {
  const { idCreatedUtc } = useParams();
  const allessehId = useMemo(() => idCreatedUtc?.replace(/_\d{13}/, ''), [idCreatedUtc]);
  const isNewCollection = useMemo(() => idCreatedUtc === 'new', [idCreatedUtc]);
  const queryClient = useQueryClient();

  const handleForceRefetch = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['CollectionDTOById'] });
  }, [queryClient]);

  // A new collection has no real id yet, so it must not acquire a lock keyed on the literal "new".
  if (isNewCollection) {
    return <CollectionEditPage allessehId={allessehId!} isNewCollection={isNewCollection} />;
  }

  return (
    <PageLockProvider
      allessehId={allessehId!}
      fragments={['publishing']}
      featureFlag={AvailableFeatureFlags.CollectionLockingEnabled}
      refetcher={handleForceRefetch}
    >
      <CollectionEditPage allessehId={allessehId!} isNewCollection={isNewCollection} />
    </PageLockProvider>
  );
};

export default CollectionEditPageWrapper;
